import bgImg from '@/assets/img/curved-images/curved9.jpg';
import { HomeLayout } from '@/components/layout/HomeLayout';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useWorkspacePaymentFailure } from '@/hooks/api/2311_workspace_payment_failure';
import { useLoading } from '@/providers/LoadingProvider';
import { Dispatch, FormEvent, SetStateAction, useEffect, useState } from 'react';
import { toastApiError, toastError } from '@/lib/toast';
import { loadStripe } from '@stripe/stripe-js';
import { AddressElement, Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useReregisterPaymentInfo } from '@/hooks/api/2310_reregister_payment_info';
import { useSetupIntentClientSecret } from '@/hooks/api/2321_setup_intent_client_secret';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

type PaymentProps = {
  setPaymentInfoUpdated: Dispatch<SetStateAction<boolean>>;
};

export const PaymentFailure = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { data, isPending, error } = useWorkspacePaymentFailure({ token: token! });
  const [paymentInfoUpdated, setPaymentInfoUpdated] = useState<boolean>(false);

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (error) {
      navigate('/sign_in');
    }
  }, [error]);

  if (data == null) return <></>;

  return (
    <>
      <HomeLayout>
        <main className="main-content mt-0">
          <div
            className="page-header align-items-start min-vh-35 pt-5 pb-11 m-3 border-radius-lg"
            style={{ backgroundImage: `url(${bgImg})` }}
          >
            <span className="mask bg-gradient-dark opacity-6"></span>
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-lg-5 text-center mx-auto">
                  <h1 className="text-white mb-2 mt-5"></h1>
                  <p className="text-lead text-white"></p>
                </div>
              </div>
            </div>
          </div>
          <div className="container">
            <div className="row mt-lg-n10 mt-md-n11 mt-n10 justify-content-center">
              <div className="col-xl-10 col-lg-9 mx-auto">
                <div className="card">
                  <div className="card-header d-flex align-items-center">
                    <h6 className="mb-0 col-md-6">決済用クレジットカード再登録</h6>
                    <div className="col-md-6 d-flex justify-content-end"></div>
                  </div>
                  <div className="card-body pt-0">
                    {paymentInfoUpdated ? (
                      <>
                        <h5 className="text-sm">クレジットカードの変更と、決済の再試行を受け付けました。</h5>
                        <p className="text-sm">
                          決済が完了すると、スペースが即時再開されます。
                          <br />
                          決済の完了はスペース管理者にメールで通知されます。
                        </p>
                      </>
                    ) : (
                      <ReregisterPaymentElement
                        token={token!}
                        identifier={data.workspace_identifier}
                        setPaymentInfoUpdated={setPaymentInfoUpdated}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </HomeLayout>
    </>
  );
};

type ReregisterPaymentElementProps = {
  token: string;
  identifier: string;
  setPaymentInfoUpdated: Dispatch<SetStateAction<boolean>>;
};
const ReregisterPaymentElement = ({ token, identifier, setPaymentInfoUpdated }: ReregisterPaymentElementProps) => {
  const { setShowLoading } = useLoading();
  const { data, isPending, error } = useSetupIntentClientSecret({ identifier });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  if (data == null) return <></>;

  return (
    <Elements
      stripe={stripePromise}
      options={{
        // mode: 'setup',
        // currency: 'jpy',
        appearance: { theme: 'stripe' },
        clientSecret: data.client_secret,
      }}
    >
      <ReregisterPayment token={token} setPaymentInfoUpdated={setPaymentInfoUpdated} />
    </Elements>
  );
};

type ReregisterPaymentProps = {
  token: string;
  setPaymentInfoUpdated: Dispatch<SetStateAction<boolean>>;
};
const ReregisterPayment = ({ token, setPaymentInfoUpdated }: ReregisterPaymentProps) => {
  const { setShowLoading } = useLoading();
  const { mutate, data, isPending, error } = useReregisterPaymentInfo();
  const [setupIntent, setSetupIntent] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    setSetupIntent(searchParams.get('setup_intent'));
  }, [searchParams]);

  useEffect(() => {
    if (setupIntent) {
      setShowLoading(true);
      mutate({ token: token, setup_intent_id: setupIntent });
    }
  }, [setupIntent]);

  useEffect(() => {
    if (data) {
      setPaymentInfoUpdated(true);
    }
    if (error) {
      setSetupIntent(null);
    }
  }, [data, error]);

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  const stripe = useStripe();
  const elements = useElements();
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return null;

    setShowLoading(true);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setShowLoading(false);
      return;
    }

    const { error: confirmSetupError } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: location.href.split('?')[0],
      },
    });

    if (confirmSetupError) {
      toastError(confirmSetupError.message || 'エラーが発生しました');
      setShowLoading(false);
      return;
    }

    // const { error: confirmationTokenError, confirmationToken } = await stripe.createConfirmationToken({
    //   elements,
    //   params: { payment_method_data: { billing_details: { address: { country: 'JP' } } } },
    // });
    //
    // if (confirmationTokenError) {
    //   toastError(confirmationTokenError.message || 'エラーが発生しました');
    //   setShowLoading(false);
    //   return;
    // }
    //
    // mutate({ token: token, confirmation_token: confirmationToken!.id });
  };

  if (setupIntent) return <></>;

  return (
    <form onSubmit={(e) => handleSubmit(e)}>
      <div className="form-group mt-4">
        <label>請求先</label>
        <div className="form-control border-0 ps-1">
          <AddressElement options={{ display: {}, mode: 'billing', allowedCountries: ['JP'] }} />
        </div>
      </div>

      <div className="form-group mt-4">
        <label>支払方法</label>
        <PaymentElement
          options={{
            layout: 'accordion',
            fields: { billingDetails: { address: { country: 'never' } } },
          }}
        />
      </div>

      <div className="card-footer d-flex align-items-center justify-content-end pt-4 pe-0 pb-0">
        <button className="btn bg-gradient-dark btn-icon">
          <i className="fas fa-check-circle"></i>変更を確定する
        </button>
      </div>
    </form>
  );
};
